import type { ProductJourney } from "@shared/analytics/productEvents";
import {
    useProductJourney,
    useProductObservation
} from "@shared/analytics/useProductJourney";
import { formatCurrency } from "@solomon-apps/shared";
import { BigDecimal } from "@solomon-labs/bigdecimal";
import { ArrowShortLeftIcon } from "@solomon-labs/design/components/icons";
import { Button, Text } from "@solomon-labs/design/components/ui";
import React, { useMemo, useState } from "react";
import {
    Area,
    ComposedChart,
    Line,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis
} from "recharts";

import { useReportingApyHistory } from "@/hooks/stats/useReportingApyHistory";

import { FDIC_SAVINGS_RATE_HISTORY } from "../data/fdicSavingsRateHistory";
import { toFdicRateObservations } from "../helpers/buildApyComparisonSeries";
import {
    buildProjectedEarningsSeries,
    formatEarnings,
    toDisplayPrecision
} from "../helpers/comparisonEarnings";

import { ApyGapChart } from "./ApyGapChart";

// Chart entry-animation timings — user line draws first (left → right),
// then the benchmark line draws after it finishes. Both ease-out.
const USER_LINE_MS = 700;
const BENCH_LINE_MS = 700;

// Entrance cascade: Back, then the tab row, then the panel. Each
// element animates exactly once and none of them nest, so the content
// ramps on a single curve rather than the product of several.
const ENTER_BACK = "fade-in 220ms ease-out 0ms forwards";
const ENTER_TABS = "fade-in 220ms ease-out 70ms forwards";
const ENTER_PANEL = "fade-in 220ms ease-out 140ms forwards";

// Tab swap, matching the Stats page's tab motion (StatsPageV3). No
// delay — the entrance beat above would read as lag on a switch.
const TAB_FADE = "fade-in 180ms cubic-bezier(0.215, 0.61, 0.355, 1) forwards";

// The same FDIC table the Rates tab plots, adapted once at module load.
// Both tabs must quote one benchmark — an Earnings tab computing against
// a different rate than the Rates tab displays is a contradiction the
// user can see.
const FDIC_OBSERVATIONS = toFdicRateObservations(FDIC_SAVINGS_RATE_HISTORY);

// One string for both the rates heading and the link that leads to it —
// the link is a question and the destination should answer the same one.
const RATES_HEADING = "How do the rates compare historically?";

// why: Button's label slot is `whitespace-nowrap`, and these link actions are
// whole questions ("How does this compare to a typical FDIC savings
// account?") that must wrap at 375px instead of overflowing the column.
// `button-label` is a documented `data-slot`, so this targets the supported
// hook rather than private markup.
const LINK_WRAP = "text-left [&_[data-slot=button-label]]:whitespace-normal";

// Fallback starting amount when the wallet holds no USDv (or is not
// connected) — the chart still needs a principal to illustrate with.
const FALLBACK_PRINCIPAL = 10_000;

/** Side of the square markers — matches ApyGapChart. */
const DOT_SIZE = 6;

function squareDot(
    fill: string,
    cx: number,
    cy: number,
    key?: string
): React.ReactElement {
    return (
        <rect
            key={key}
            x={cx - DOT_SIZE / 2}
            y={cy - DOT_SIZE / 2}
            width={DOT_SIZE}
            height={DOT_SIZE}
            fill={fill}
        />
    );
}

/** Hover marker — recharts defaults `activeDot` to a circle, so both
 *  charts override it to stay square. */
function activeDotRenderer(fill: string) {
    return function ActiveDot(props: unknown): React.ReactElement {
        const { cx, cy } = props as { cx?: number; cy?: number };
        if (cx === undefined || cy === undefined) return <g />;
        return squareDot(fill, cx, cy);
    };
}

/** Hover tooltip surface — the recipe the shared Charts components use
 *  (`bg-surface-inverse-raised` + `foreground-inverse`), replacing the
 *  inline `contentStyle` with raw font values that used to live here. */
function EarningsTooltip({
    active,
    label,
    payload,
    benchLabel
}: {
    active?: boolean;
    label?: number;
    payload?: ReadonlyArray<{ dataKey?: string | number; value?: number }>;
    benchLabel: string;
}): React.ReactElement | null {
    if (!active || label === undefined || !payload) return null;

    const valueOf = (key: string): number =>
        payload.find((item) => item.dataKey === key)?.value ?? 0;

    return (
        <div className="flex min-w-48 flex-col gap-2 bg-surface-inverse-raised px-3 py-2">
            <Text variant="small" className="text-foreground-imprint">
                {new Date(label).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                    year: "numeric"
                })}
            </Text>
            <div className="flex items-center justify-between gap-6">
                <Text variant="small" className="text-foreground-inverse">
                    USDv
                </Text>
                <Text variant="number" className="text-foreground-inverse">
                    {formatEarnings(valueOf("user"))}
                </Text>
            </div>
            <div className="flex items-center justify-between gap-6">
                <Text variant="small" className="text-foreground-inverse">
                    {benchLabel}
                </Text>
                <Text variant="number" className="text-foreground-inverse">
                    {formatEarnings(valueOf("bench"))}
                </Text>
            </div>
        </div>
    );
}

// Each question maps to a benchmark line + a friendly explainer label.
// The chart's user line stays constant (their YaaS APY); only the
// benchmark line changes when the user picks a different question.
export interface ComparisonQuestion {
    key: string;
    /** Headline question shown in the question list and as the title
     *  when this question is selected. */
    label: string;
    /** Fallback benchmark APY as a percent (e.g. 4.5 for 4.5%).
     *
     *  The savings projection uses the latest observation from
     *  `FDIC_SAVINGS_RATE_HISTORY`, the same source the Rates tab plots.
     *  This field remains the flat-rate placeholder for disabled benchmarks
     *  until each has a product-approved current-rate source. */
    benchApy: number;
    /** Short label rendered as a tooltip-ready name for the bench line. */
    benchLabel: string;
    /** Whether the question is offered to the user. Definitions for
     *  benchmarks we aren't offering yet stay here (the chart renders
     *  them unchanged once flipped on) but are never selectable. */
    enabled: boolean;
}

export const COMPARISON_QUESTIONS: ComparisonQuestion[] = [
    {
        key: "savings",
        label: "How does this compare to a typical FDIC savings account?",
        benchApy: 0.45,
        benchLabel: "FDIC",
        enabled: true
    },
    // T-Bill and AAVE are hard-coded placeholder rates. They stay
    // defined but unwired until each has a real rate source, so the
    // panel never shows a benchmark we can't back.
    {
        key: "tbill",
        label: "What about a 3-month T-Bill?",
        benchApy: 5.2,
        benchLabel: "3mo T-Bill",
        enabled: false
    },
    {
        key: "aave",
        label: "How does this compare to AAVE USDC borrow rates?",
        benchApy: 6.5,
        benchLabel: "AAVE USDC",
        enabled: false
    }
];

/** The questions the explorer actually offers. Non-empty by
 *  construction — the savings benchmark is always enabled. */
export const SELECTABLE_COMPARISON_QUESTIONS: ComparisonQuestion[] =
    COMPARISON_QUESTIONS.filter((q) => q.enabled);

function formatDateLabel(timestamp: number): string {
    return new Intl.DateTimeFormat("en-US", {
        month: "short",
        year: "numeric"
    }).format(new Date(timestamp));
}

interface RetailYaasComparisonExplorerProps {
    analytics?: ProductJourney;
    walletAddress?: string;
    /** Current resolved protocol APY as a percent number (e.g. 3.07 for
     *  3.07%), or null while the live value is unavailable. */
    yaasApy: number | null;
    /** Selected wallet's paid annual rate. Takes priority over the published rate. */
    historicalAprPct?: string | null;
    /** Connected wallet's USDv balance, used to seed the starting
     *  amount so the comparison opens on the user's real position
     *  rather than a round illustrative number. */
    usdvBalance?: number;
    /** Called when the user dismisses the panel — page hands control
     *  back to the action card. */
    onClose: () => void;
    /** Optional question key to open with. Defaults to the first
     *  entry in SELECTABLE_COMPARISON_QUESTIONS; a key that isn't
     *  currently offered falls back to that same entry. */
    initialKey?: string;
}

type ExplorerTab = "rates" | "earnings";

/**
 * Comparison explorer (expanded view only — the page owns the
 * collapsed invitation and the open/close transition).
 *
 * Two tabs:
 * - "Rates": the ApyGapChart — real USDv 7-day APY history vs the FDIC
 *   national savings rate, year to date.
 * - "Earnings": the user's cumulative earnings line vs a benchmark line
 *   for a single selected question, on a user-editable starting amount.
 *   When more than one benchmark is offered, a list of follow-up
 *   questions below the chart swaps the benchmark in place.
 *
 * Earnings data is an illustration at constant rates for one year.
 * A wallet's historical rate uses simple interest. Published and benchmark
 * APYs use daily growth. Historical rates are not a forecast.
 */
export function RetailYaasComparisonExplorer({
    analytics: entryAnalytics,
    walletAddress,

    yaasApy,
    historicalAprPct = null,
    usdvBalance,
    onClose,
    initialKey
}: RetailYaasComparisonExplorerProps): React.ReactElement {
    const localAnalytics = useProductJourney(
        "earn_compare",
        "rewards_footer",
        walletAddress
    );
    const analytics = entryAnalytics ?? localAnalytics;
    useProductObservation(analytics, "viewed");
    const [tab, setTab] = useState<ExplorerTab>("earnings");
    // Panels animate on the entrance beat until the user works the
    // tabs; after that a swap owns the motion. Flipped in the same
    // render as the tab change, so it never restarts a running fade.
    const [switched, setSwitched] = useState(false);
    const [selectedKey, setSelectedKey] = useState<string>(
        initialKey ?? SELECTABLE_COMPARISON_QUESTIONS[0].key
    );

    // Fetched only once the explorer opens (this component only mounts
    // then); React Query dedupes/caches with any other consumer.
    const yieldData = useReportingApyHistory("all");
    const apyObservations = useMemo(
        () =>
            yieldData.allTimeApyHistory.map(({ timestamp, value }) => ({
                timestamp,
                ratePct: value
            })),
        [yieldData.allTimeApyHistory]
    );

    const selected =
        SELECTABLE_COMPARISON_QUESTIONS.find((q) => q.key === selectedKey) ??
        SELECTABLE_COMPARISON_QUESTIONS[0];

    // Empty while only one benchmark is offered — the follow-up list
    // is dropped entirely rather than rendering an empty stack.
    const followUps = SELECTABLE_COMPARISON_QUESTIONS.filter(
        (q) => q.key !== selected.key
    );

    // Freeze "now" per mount so editing the amount doesn't shift the
    // series timestamps on every keystroke.
    const now = useMemo(() => Date.now(), []);
    // The wallet's balance is the whole point of the comparison, so it
    // is the principal outright. The fallback only covers a wallet with
    // no USDv, where a zero balance would flatten both lines to nothing.
    const principal =
        usdvBalance !== undefined && usdvBalance > 0
            ? usdvBalance
            : FALLBACK_PRINCIPAL;
    // The savings comparison uses the latest observed FDIC rate. Other
    // benchmarks remain disabled until they have product-approved sources.
    const benchmarkApy =
        selected.key === "savings"
            ? (FDIC_OBSERVATIONS.at(-1)?.ratePct ?? selected.benchApy)
            : selected.benchApy;
    const historicalRate =
        historicalAprPct === null
            ? null
            : new BigDecimal(historicalAprPct).toNumber();
    const usesHistoricalRate =
        historicalRate !== null &&
        Number.isFinite(historicalRate) &&
        historicalRate >= 0;
    const projectionRate = usesHistoricalRate ? historicalRate : yaasApy;
    const earnings = useMemo(
        () =>
            projectionRate === null
                ? { points: [], startTimestamp: null, days: 0 }
                : buildProjectedEarningsSeries(
                      principal,
                      projectionRate,
                      benchmarkApy,
                      now,
                      usesHistoricalRate ? "apr" : "apy"
                  ),
        [benchmarkApy, now, principal, projectionRate, usesHistoricalRate]
    );
    // `gapRange` is the band between the two lines — same treatment as
    // the Rates chart, where the shaded area is the advantage itself.
    const chartData = useMemo(
        () =>
            earnings.points.map((point) => ({
                ...point,
                gapRange: [point.bench, point.user] as [number, number]
            })),
        [earnings.points]
    );

    // Missing rates are unavailable, not a known zero return.
    const hasEarnings = chartData.length > 0;
    const periodLabel = "over the next year";
    const lastPoint = chartData[chartData.length - 1];
    const userLast = lastPoint?.user ?? 0;
    const benchLast = lastPoint?.bench ?? 0;
    // Derived from the rounded figures, not the raw ones, so the
    // headline minus the parenthetical equals the gap as shown.
    const delta = toDisplayPrecision(userLast) - toDisplayPrecision(benchLast);
    const deltaBehind = delta < 0;

    const startLabel = chartData[0]
        ? formatDateLabel(chartData[0].timestamp)
        : "";
    const endLabel = lastPoint ? formatDateLabel(lastPoint.timestamp) : "";

    const earningsTab = (
        <div className="flex flex-col gap-5">
            {/* Headline: cumulative earnings on top, delta vs the
                benchmark below. mt-4 keeps it tight against the
                question title so the chart sits closer to the
                surrounding text. */}
            <div className="flex flex-col gap-1 tabular-nums mt-4">
                {/* Lead-in to the figure: the principal it was
                    computed on — the wallet's own USDv balance, stated
                    rather than entered. `body` is the text-base rung of
                    the ladder; at display scale it competed with the
                    figure instead of introducing it. `mb-3` separates
                    the two rather than the stack's tight `gap-1`. */}
                <Text variant="body" className="text-foreground-base mb-3">
                    Your {formatCurrency(principal)} could earn
                </Text>
                {/* Signed: the figure is rewards paid on top of the
                    balance, not the balance itself. Always a gain —
                    accrual only ever adds — so the sign is a fixed
                    "+" rather than a computed one. */}
                {/* Keep the period readable when larger earnings fill a
                    narrow action column. */}
                <div className="flex flex-wrap items-baseline gap-1.5">
                    <Text variant="number-lg" className="text-foreground-base">
                        {hasEarnings ? `+${formatEarnings(userLast)}` : "—"}
                    </Text>
                    {hasEarnings && (
                        <Text
                            variant="small"
                            render={<span />}
                            className="text-foreground-muted"
                        >
                            {periodLabel}
                        </Text>
                    )}
                </div>
                {/* Text wraps the icon so `1lh` on the img resolves
                    to the small variant's line-height, keeping the
                    arrow visually the same height as the digits. */}
                {/* Delta direction drives icon + color — a benchmark
                    that out-earns the user must not read as a green
                    gain ("↑$-1"). */}
                <Text
                    variant="small"
                    render={<span />}
                    className={`inline-flex items-center gap-0 ${
                        hasEarnings ? "" : "invisible"
                    } ${deltaBehind ? "text-error-base" : "text-success-base"}`}
                >
                    <img
                        src={
                            deltaBehind
                                ? "/img/icons/down-short-red.svg"
                                : "/img/icons/up-short-green.svg"
                        }
                        alt=""
                        className="h-3.5 w-auto"
                        aria-hidden
                    />
                    {formatEarnings(Math.abs(delta))} vs {selected.benchLabel} (
                    {formatEarnings(benchLast)})
                </Text>
            </div>

            {/* Minimal chart — user line solid, bench line dashed,
                no visible axes or grid. */}
            {hasEarnings ? (
                <div className="flex flex-col">
                    <ResponsiveContainer width="100%" height={120} minWidth={0}>
                        <ComposedChart
                            data={chartData}
                            margin={{ top: 4, right: 4, left: 4, bottom: 0 }}
                        >
                            <XAxis
                                dataKey="timestamp"
                                type="number"
                                scale="time"
                                domain={["dataMin", "dataMax"]}
                                hide
                            />
                            {/* Scale to the projected earnings themselves. A
                            fixed dollar of headroom flattens both lines
                            when the wallet holds only a few dollars. */}
                            <YAxis
                                domain={[0, "dataMax"]}
                                padding={{ top: 8, bottom: 8 }}
                                hide
                            />
                            {/* Hover tooltip — shows the user vs benchmark
                            value at the hovered date. */}
                            <Tooltip
                                cursor={{
                                    stroke: "rgb(var(--color-line-base))",
                                    strokeDasharray: "3 3"
                                }}
                                content={(props) => {
                                    const p = props as {
                                        active?: boolean;
                                        label?: number;
                                        payload?: ReadonlyArray<{
                                            dataKey?: string | number;
                                            value?: number;
                                        }>;
                                    };
                                    return (
                                        <EarningsTooltip
                                            active={p.active}
                                            label={p.label}
                                            payload={p.payload}
                                            benchLabel={selected.benchLabel}
                                        />
                                    );
                                }}
                            />
                            {/* Shaded advantage band, matching the Rates
                            chart. Neutral rather than accent-tinted: it
                            covers the whole plot, and an ember wash at
                            any legible opacity would overwhelm a panel
                            this small. */}
                            <Area
                                type="monotone"
                                dataKey="gapRange"
                                stroke="none"
                                fill="rgb(var(--color-foreground-base))"
                                fillOpacity={0.05}
                                activeDot={false}
                                connectNulls
                                isAnimationActive={false}
                                tooltipType="none"
                            />
                            {/* User line draws first (left → right). No
                            static end marker — the square only appears
                            on hover, matching the Rates chart. */}
                            <Line
                                type="monotone"
                                dataKey="user"
                                stroke="rgb(var(--color-accent-base))"
                                strokeWidth={1.5}
                                isAnimationActive
                                animationDuration={USER_LINE_MS}
                                animationBegin={0}
                                animationEasing="ease-out"
                                activeDot={activeDotRenderer(
                                    "rgb(var(--color-accent-base))"
                                )}
                                dot={false}
                            />
                            {/* Benchmark line draws after the user line
                            finishes — same duration, delayed start. */}
                            <Line
                                type="monotone"
                                dataKey="bench"
                                stroke="rgb(var(--color-tint-base))"
                                strokeWidth={1.25}
                                strokeDasharray="4 4"
                                isAnimationActive
                                animationDuration={BENCH_LINE_MS}
                                animationBegin={USER_LINE_MS}
                                animationEasing="ease-out"
                                activeDot={activeDotRenderer(
                                    "rgb(var(--color-tint-base))"
                                )}
                                dot={false}
                            />
                        </ComposedChart>
                    </ResponsiveContainer>
                    <div className="flex justify-between pt-2 min-h-5 border-t border-line-base">
                        <Text
                            variant="small"
                            render={<span />}
                            className="text-foreground-base"
                        >
                            {startLabel}
                        </Text>
                        <Text
                            variant="small"
                            render={<span />}
                            className="text-foreground-base"
                        >
                            {endLabel}
                        </Text>
                    </div>
                    <ul
                        aria-label="Chart lines"
                        className="flex flex-wrap items-center gap-x-4 gap-y-2 pt-2"
                    >
                        <li className="flex items-center gap-2">
                            <span
                                aria-hidden
                                className="w-5 border-t-2 border-accent-base"
                            />
                            <Text
                                variant="small"
                                render={<span />}
                                className="text-foreground-muted"
                            >
                                USDv
                            </Text>
                        </li>
                        <li className="flex items-center gap-2">
                            <span
                                aria-hidden
                                className="w-5 border-t-2 border-dashed border-tint-base"
                            />
                            <Text
                                variant="small"
                                render={<span />}
                                className="text-foreground-muted"
                            >
                                {selected.benchLabel}
                            </Text>
                        </li>
                    </ul>
                    <Text
                        variant="small"
                        className="text-foreground-muted mt-4"
                    >
                        {usesHistoricalRate
                            ? `Illustration using your ${new BigDecimal(historicalAprPct!).round(1).toString()}% historical APY for one year, without reinvesting rewards.`
                            : `Illustration using the published ${projectionRate}% APY for one year.`}{" "}
                        Assumes the same rates and no deposits or withdrawals.
                    </Text>
                </div>
            ) : (
                <Text variant="small" className="text-foreground-muted">
                    A USDv rate is not available for this comparison.
                </Text>
            )}

            {/* Additional questions below the chart — each click
                swaps the benchmark line in place. Small-text
                underline links stacked with default line-height
                spacing (no `gap-N`) to match the position card's
                YaasPositionsFooter, which sets the same compact
                stack pattern. `mt-4` keeps the gap from the chart
                tight. */}
            {followUps.length > 0 && (
                <div className="flex flex-col mt-4 items-start">
                    {followUps.map((q) => (
                        <Button
                            key={q.key}
                            variant="link"
                            size="sm"
                            onClick={() => {
                                analytics.send("selected", { target: q.key });
                                setSelectedKey(q.key);
                            }}
                            className={LINK_WRAP}
                        >
                            {q.label}
                        </Button>
                    ))}
                </div>
            )}
        </div>
    );

    return (
        <div className="flex flex-col gap-5">
            {/* Entrance cascade. The page slides the whole panel in
                (page-fade-up on the wrapper in RetailYaasLandingPage);
                its parts then resolve in reading order — Back, tabs,
                content — instead of arriving as one slab. Opacity only,
                so these compose with that outer slide rather than
                fighting it with a second translate. `data-motion-section`
                opts each into the prefers-reduced-motion override in
                app.css. */}
            <div
                data-motion-section
                className="self-start"
                style={{
                    opacity: 0,
                    animation: ENTER_BACK
                }}
            >
                <Button
                    variant="link"
                    size="sm"
                    onClick={() => {
                        analytics.send("closed", { close_method: "back" });
                        onClose();
                    }}
                >
                    <span className="inline-flex items-center gap-0.5">
                        <ArrowShortLeftIcon className="size-3" />
                        Back
                    </span>
                </Button>
            </div>

            {/* Section heading — sits above the tabs and swaps with
                them, so it always describes the panel on screen. The
                Rates panel's own sentence moved up here rather than
                being duplicated. */}
            <Text
                variant="small"
                render={<span />}
                className="text-foreground-base"
                data-motion-section
                style={{
                    opacity: 0,
                    animation: ENTER_TABS
                }}
            >
                {tab === "rates" ? RATES_HEADING : selected.label}
            </Text>

            {/* Both panels share one grid cell, so the section is
                always as tall as the taller tab. The action column
                centers its content vertically — letting the panel
                resize on a tab switch would slide the whole thing,
                Back button included, by the height difference.
                The inactive panel keeps its layout box (`invisible`,
                never `hidden`) so its ResponsiveContainer can still
                measure a width, and `inert` takes it out of the tab
                order and the accessibility tree while it's off. */}
            <div className="grid">
                <div
                    key={`rates-${tab}`}
                    data-motion-tab
                    className={`col-start-1 row-start-1 flex flex-col gap-4 ${
                        tab === "rates" ? "" : "invisible"
                    }`}
                    style={{
                        opacity: 0,
                        animation: switched ? TAB_FADE : ENTER_PANEL
                    }}
                    inert={tab !== "rates"}
                >
                    <ApyGapChart
                        usdvObservations={apyObservations}
                        loading={yieldData.isLoading}
                    />
                </div>
                {/* Keyed on the active tab so the earnings lines
                    re-draw each time the tab is selected — the panel
                    is mounted the whole time for sizing, so without
                    the remount the entry animation would play once,
                    unseen, behind the Rates tab. */}
                <div
                    key={`earnings-${tab}`}
                    data-motion-tab
                    className={`col-start-1 row-start-1 ${
                        tab === "earnings" ? "" : "invisible"
                    }`}
                    style={{
                        opacity: 0,
                        animation: switched ? TAB_FADE : ENTER_PANEL
                    }}
                    inert={tab !== "earnings"}
                >
                    {earningsTab}
                </div>
            </div>

            {/* View switch — replaces the tab row. Same underlined
                small-text link as YaasPositionsFooter, and it names the
                view it leads to rather than labelling the one you are
                on. `mt-7` stacks on the parent's `gap-5` for 48px of
                separation — the `mt-12` rhythm the other section
                uses. */}
            <Button
                variant="link"
                size="sm"
                onClick={() => {
                    analytics.send("tab_selected", {
                        previous_tab: tab,
                        tab: tab === "earnings" ? "rates" : "earnings"
                    });
                    setSwitched(true);
                    setTab(tab === "earnings" ? "rates" : "earnings");
                }}
                className={`self-start mt-7 ${LINK_WRAP}`}
                data-motion-section
                style={{
                    opacity: 0,
                    animation: ENTER_TABS
                }}
            >
                {tab === "earnings" ? RATES_HEADING : selected.label}
            </Button>
        </div>
    );
}
